import { PropsWithChildren, useEffect } from "react";
import { FieldPath, FieldValues, SetFieldValue } from "react-hook-form";
import styled from "styled-components";
import { GRAY, LIGHT_GRAY, NEAR_WHITE } from "../../config";
import { BaseField, BaseFieldProps } from "./BaseField";

interface TextAreaProps {
  height?: string;
  border?: string;
  backgroundColor?: string;
}

const TextArea = styled.textarea<TextAreaProps>`
  width: 100%;
  padding: 9px 15px;
  box-sizing: border-box;
  border-radius: 4px;
  border: 1px solid ${LIGHT_GRAY};
  color: ${NEAR_WHITE};
  font-size: 14px;
  transition: 0.2s ease;
  outline: none;
  resize: none;
  font-family: Roboto;

  height: ${({ height }) => (height ? `${height}px` : `112px`)};
  border: ${({ border }) => border ?? `1px solid ${LIGHT_GRAY}`};
  background-color: ${({ backgroundColor }) => backgroundColor ?? GRAY};

  ::placeholder {
    color: ${NEAR_WHITE};
  }

  &:focus {
    border-color: ${LIGHT_GRAY};

    & + svg {
      color: ${LIGHT_GRAY};
    }
  }

  &:disabled {
    border: 0;
    padding: 0;
  }
`;

export interface TextAreaFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends BaseFieldProps<TFieldValues, TName> {
  placeholder?: string;
  height?: string;
  backgroundColor?: string;
  border?: string;
  onChange?: (value: string) => void;
  readonly?: boolean;
  valueState?: SetFieldValue<TFieldValues>;
  maxLength?: number;
}
export function TextAreaField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  label,
  name,
  placeholder,
  defaultValue,
  height,
  backgroundColor,
  border,
  rules,
  form,
  readOnly,
  onChange,
  valueState,
  maxLength,
  fieldClassName,
}: PropsWithChildren<TextAreaFieldProps<TFieldValues, TName>>) {
  useEffect(() => {
    valueState && form.setValue(name, valueState);
  }, [valueState]);
  return (
    <BaseField
      form={form}
      name={name}
      label={label}
      defaultValue={defaultValue}
      rules={rules}
      readOnly={readOnly}
      fieldClassName={`text-area-field ${fieldClassName}`}
      render={({ field }) => (
        <TextArea
          data-testid="textArea"
          readOnly={readOnly}
          height={height}
          name={name}
          placeholder={!readOnly ? placeholder : ""}
          onChange={(e) => {
            field.onChange(e.target.value);
            onChange?.(e.target.value);
          }}
          backgroundColor={backgroundColor}
          border={border}
          disabled={readOnly}
          value={valueState || field.value}
          maxLength={maxLength}
        />
      )}
    />
  );
}

TextAreaField.defaultProps = {
  defaultValue: "",
};
